import React from 'react';
import {
  useDataModel,
  QueryProvider,
  extend,
  params,
  id as idType,
  string as stringType,
  number as numberType,
  shape,
  arrayOf,
  bool as boolType,
} from '@thd-nucleus/data-sources';
import {
  KeyCategories,
  KnowYourBenefits,
  PriceBookDetails,
  PriceBookHeader,
  PriceBookSpendGoals,
  SpendHighlights,
  SpendMeters,
  TemporaryPlacementMessage,
  TopSavings,
} from '@thd-olt-component-react/ocm_pd_component_myaccount-pricebook';
import {
  PageErrorRedirector,
  PageProviders,
} from '../components/shared/page-layout';
import { PricingBenefitsPage } from './PricingBenefitsPage';
import { PricingBookPage } from './PricingBookPage';
import { PricingErrorPage } from './PricingErrorPage';
import { getSvocId, getUserId, isB2BUser } from '../utils/user';
import { getChannelID } from '../utils/NetworkUtil';
import { useHasBenefitTag } from '../hooks/use-has-benefit-tag';
import { useRewardsAndOffers } from '../hooks/use-rewards-and-offers';
import { PricingProvider } from '../contexts';
import { RewardsAndOffersProvider } from '../contexts/api/rewardsAndOffers-context';
import {
  checkV1Error,
  checkV2Error,
  collectAllGQLErrorCodes,
} from '../utils/errorUtils';

type ProTiersBenefitTagsShape = {
  customerInfo: {
    company: {
      businessName: string;
    };
  };
  proTiers: {
    benefitTags: Array<{
      tagName: string;
      value: string;
    }>;
  };
};

type loyaltyMembershipInfoModel = {
  loyaltyMembershipInfo: ReturnType<typeof params> & {
    shape: (arg: ProTiersBenefitTagsShape) => ReturnType<typeof params>;
  };
};

type PricingPageContentType = React.FC & {
  dataModel: loyaltyMembershipInfoModel;
};

const PricingPageContent: PricingPageContentType = () => {
  const isB2B = isB2BUser();
  const channelId = getChannelID();
  const userId = getUserId();
  const customerAccountId = getSvocId();

  return (
    <PageErrorRedirector>
      <RewardsAndOffersProvider>
        <PricingProvider shouldShowPricingBookPage={isB2B}>
          <PricingBenefitsPageContent
            userId={userId}
            customerAccountId={customerAccountId}
            channelId={channelId}
            isB2B={isB2B}
          />
        </PricingProvider>
      </RewardsAndOffersProvider>
    </PageErrorRedirector>
  );
};

interface PricingBenefitsPageContentProps {
  userId: string | null;
  customerAccountId: string | null;
  channelId: number;
  isB2B: boolean;
}

const PricingBenefitsPageContent = ({
  userId,
  customerAccountId,
  channelId,
  isB2B,
}: PricingBenefitsPageContentProps) => {
  const { generalError: contextError, showV2PriceBook } = useRewardsAndOffers();
  const {
    data,
    error: graphQlError,
    loading,
  } = useDataModel('loyaltyMembershipInfo', {
    variables: {
      userId,
      customerAccountId,
      channelId,
    },
    skip: !isB2B || !userId || !customerAccountId,
    ssr: false,
    context: {
      withAuth: true,
    },
  });

  const hasPriceBookBenefit = useHasBenefitTag(
    data?.loyaltyMembershipInfo?.proTiers?.benefitTags,
    'PriceBooks',
  );

  const temporaryEnrollmentStatus =
    !!data?.loyaltyMembershipInfo?.priceBook?.tempBook;

  // While loading, render nothing to avoid flickering between states
  if (loading) return null;

  // Combine errors from GraphQL query and context (RewardsAndOffers API)
  const pricingError = graphQlError || contextError;

  const allGQLCodes = collectAllGQLErrorCodes(graphQlError);
  const hasContextError = Boolean(contextError);
  const hasV1Error = checkV1Error(allGQLCodes) || hasContextError;
  const hasV2Error = checkV2Error(allGQLCodes) || hasContextError;

  // tempBook: Check if user is enrolled in temporary enrollment
  if (temporaryEnrollmentStatus) {
    if (hasV1Error) {
      return (
        <PageProviders page="PricingErrorPage">
          <PricingErrorPage error={pricingError} />
        </PageProviders>
      );
    }
    return (
      <PageProviders page="PricingBooksPage">
        <PricingBookPage temporaryEnrollment={temporaryEnrollmentStatus} />
      </PageProviders>
    );
  }

  // Explicitly going through all combinations of flags and errors to determine which page to show in lines 158-232
  // This code will get refactored once V1 and pricing benefits is fully deprecated

  // priceBook: User has standard price book benefit
  if (hasPriceBookBenefit) {
    // priceBook + V2 switch disabled + no errors → Show pricing books page
    if (!showV2PriceBook && !hasV2Error && !hasV1Error) {
      return (
        <PageProviders page="PricingBooksPage">
          <PricingBookPage temporaryEnrollment={temporaryEnrollmentStatus} />
        </PageProviders>
      );
    }
    // priceBook + V2 switch enabled + no errors → Show pricing books page
    if (showV2PriceBook && !hasV2Error && !hasV1Error) {
      return (
        <PageProviders page="PricingBooksPage">
          <PricingBookPage temporaryEnrollment={temporaryEnrollmentStatus} />
        </PageProviders>
      );
    }
    // priceBook + V2 switch enabled + both V1 and V2 errors → Show error page
    if (showV2PriceBook && hasV2Error && hasV1Error) {
      return (
        <PageProviders page="PricingErrorPage">
          <PricingErrorPage error={pricingError} />
        </PageProviders>
      );
    }
    // priceBook + V2 switch disabled + V2 error + no V1 error → Show pricing books page
    if (!showV2PriceBook && hasV2Error && !hasV1Error) {
      return (
        <PageProviders page="PricingBooksPage">
          <PricingBookPage temporaryEnrollment={temporaryEnrollmentStatus} />
        </PageProviders>
      );
    }
    // priceBook + V2 switch enabled + V2 error + no V1 error → Show error page
    if (showV2PriceBook && hasV2Error && !hasV1Error) {
      return (
        <PageProviders page="PricingErrorPage">
          <PricingErrorPage error={pricingError} />
        </PageProviders>
      );
    }
    // priceBook + V2 switch disabled + V1 error + no V2 error → Show error page
    if (!showV2PriceBook && hasV1Error && !hasV2Error) {
      return (
        <PageProviders page="PricingErrorPage">
          <PricingErrorPage error={pricingError} />
        </PageProviders>
      );
    }
    // priceBook + V2 switch enabled + V1 error + no V2 error → Show pricebook page
    if (showV2PriceBook && hasV1Error && !hasV2Error) {
      return (
        <PageProviders page="PricingBooksPage">
          <PricingBookPage temporaryEnrollment={temporaryEnrollmentStatus} />
        </PageProviders>
      );
    }
    // priceBook + V2 switch disabled + V1 error + V2 error → Show error page
    if (!showV2PriceBook && hasV1Error && hasV2Error) {
      return (
        <PageProviders page="PricingErrorPage">
          <PricingErrorPage error={pricingError} />
        </PageProviders>
      );
    }
  }

  // Default case: User does not have PriceBooks benefit (has PreferredPricing or no benefits) - show legacy experience
  return (
    <PageProviders page="PricingBenefitsPage">
      <PricingBenefitsPage />
    </PageProviders>
  );
};

PricingPageContent.dataModel = extend(
  {
    loyaltyMembershipInfo: params({
      userId: idType().isRequired(),
      customerAccountId: idType().isRequired(),
      channelId: numberType().isRequired(),
    }).shape({
      customerInfo: shape({
        company: shape({
          businessName: stringType(),
        }),
      }),
      priceBook: shape({
        tempBook: boolType(),
      }).isRequired(),
      proTiers: shape({
        benefitTags: arrayOf(
          shape({
            tagName: stringType(),
            value: stringType(),
          }),
        ),
      }),
    }),
  },
  PriceBookHeader,
  SpendMeters,
  SpendHighlights,
  KeyCategories,
  KnowYourBenefits,
  PriceBookDetails,
  PriceBookSpendGoals,
  TemporaryPlacementMessage,
  TopSavings,
);

export const PricingPageWrapper = () => {
  return (
    <QueryProvider cacheKey="PricingPageWrapper">
      <PricingPageContent />
    </QueryProvider>
  );
};

PricingPageWrapper.displayName = 'PricingPageWrapper';
